(async () => {
    interface InterviewData {
        linkPath: string;
        title: string;
        personInfo: string;
        personName: string;
        jobCategory: string;
        imagePath: string;
        tags: string[];
        employmentType?: string;
        workPlace?: string;
    }

    const dataUrl = '/careers/recruit/interview/json/interview.json';
    let interviewData: InterviewData[] = [];

    try {
        const fetchData = await fetch(dataUrl);
        if (!fetchData.ok) {
            throw new Error('データの読み込みに失敗しました');
        }
        interviewData = (await fetchData.json()) as InterviewData[];
    } catch (error) {
        if (error instanceof Error) {
            throw new Error('データのロード中にエラーが発生しました');
        }
    }

    // DOM取得
    const controlAreaRoot = document.querySelector<HTMLElement>('.js-filter-control');
    if (!controlAreaRoot) {
        return;
    }

    const controlEmployment = controlAreaRoot.querySelectorAll<HTMLInputElement>('input[name="employmentType"]');
    const controlPlace = controlAreaRoot.querySelectorAll<HTMLInputElement>('input[name="workPlace"]');
    const controlTags = controlAreaRoot.querySelectorAll<HTMLInputElement>('input[name="tags"]');
    const controlResetBtn = controlAreaRoot.querySelector<HTMLButtonElement>('.js-filter-reset');
    const outputAreaRoot = document.querySelector<HTMLElement>('.js-filter-output__render');
    const template = document.querySelector<HTMLTemplateElement>('.js-filter-output__template');
    // jobCategory別のクラス適用

    function getTypeModifierClasses(jobCategory: string): string {
        const categoryMap: Record<string, string> = {
            研究: 'm-panel-person-card--type-orange',
            'キャリア：研究': 'm-panel-person-card--type-orange',
            生産技術: 'm-panel-person-card--type-lightblue',
            品質: 'm-panel-person-card--type-lightblue',
            技術: 'm-panel-person-card--type-lightblue',
            'キャリア：生産': 'm-panel-person-card--type-lightblue',
            'キャリア：品質': 'm-panel-person-card--type-lightblue',
            クオリティサイエンス: 'm-panel-person-card--type-lightblue',
            生産: 'm-panel-person-card--type-lightblue',
            開発: 'm-panel-person-card--type-lightgreen',
            ファーマコビジランス: 'm-panel-person-card--type-lightgreen',
            MR: 'm-panel-person-card--type-green',
            営業: 'm-panel-person-card--type-green',
            コーポレートスタッフ: 'm-panel-person-card--type-blue',
            コーポレート: 'm-panel-person-card--type-blue',
        };

        return categoryMap[jobCategory] || '';
    }

    // 検索結果0件メッセージ
    function updateFilterMessage(totalNum: number): void {
        const messageElement = document.querySelector<HTMLElement>('.js-filter-output_message');

        if (totalNum !== 0) {
            messageElement?.remove();

            return;
        }

        // 検索結果が0件かつメッセージなし
        if (!messageElement) {
            const newMessage = document.createElement('p');
            newMessage.classList.add('m-text', 'align-c', 'js-filter-output_message');
            newMessage.innerHTML = '<em>検索条件に適した結果がありません。条件を変更してご確認ください。</em>';
            outputAreaRoot?.parentNode?.insertBefore(newMessage, outputAreaRoot);
        }
    }

    // 表示個数に合わせて背景エレメント画像出し入れ
    function toggleBackgroundClasses(itemCount: number): void {
        // 表示個数が3つ以下（1段）の場合はエレメント上のみ
        outputAreaRoot?.classList.toggle('uniq-interview-index--bg-bottom-none', itemCount <= 3);
        // 表示個数が6つ以下（2段）の場合はエレメント上下のみ
        outputAreaRoot?.classList.toggle('uniq-interview-index--bg-middle-none', itemCount <= 6);
    }

    // データのレンダリング
    function renderData(dataToRender: InterviewData[]): void {
        if (!outputAreaRoot || !template) {
            return;
        }

        outputAreaRoot.innerHTML = '';

        dataToRender.forEach((data, index) => {
            // テンプレートを複製
            const clone = template.content.cloneNode(true) as DocumentFragment;

            const item = clone.querySelector<HTMLElement>('.js-filter-output__item');
            const link = clone.querySelector<HTMLAnchorElement>('.js-filter-output__link');
            const title = clone.querySelector<HTMLElement>('.js-filter-output__title');
            const info = clone.querySelector<HTMLElement>('.js-filter-output__info');
            const category = clone.querySelector<HTMLElement>('.js-filter-output__category');
            const name = clone.querySelector<HTMLElement>('.js-filter-output__name');
            const image = clone.querySelector<HTMLImageElement>('.js-filter-output__image');
            const tagList = clone.querySelector<HTMLElement>('.js-filter-output__tag-list');

            if (!link || !title || !info || !name || !category || !image || !tagList || !item) {
                return;
            }

            // 各要素を挿入
            link.href = data.linkPath;
            title.textContent = data.title;
            info.textContent = data.personInfo;
            name.textContent = data.personName;
            category.textContent = data.jobCategory;
            image.src = data.imagePath;
            data.tags.forEach((tag) => {
                const li = document.createElement('li');
                li.innerHTML = `<span>#</span>${tag}`;
                li.classList.add('m-panel-person-card__item');
                tagList.appendChild(li);
            });

            const delayTime = 0.05 * (index + 1);
            item.style.setProperty('--delay-time', `${delayTime}s`);

            const typeModifier = getTypeModifierClasses(data.jobCategory);
            if (typeModifier) {
                item.classList.add(typeModifier);
            }

            outputAreaRoot.appendChild(clone);
        });

        updateFilterMessage(dataToRender.length);
        toggleBackgroundClasses(dataToRender.length);
    }

    function getFilterInputs(): void {
        const checkedEmployment = Array.from(controlEmployment || []).find((input) => input.checked)?.value;
        const checkedPlace = Array.from(controlPlace || []).find((input) => input.checked)?.value;
        const checkedTags = Array.from(controlTags || []).filter((input) => input.checked).map((input) => input.value);

        // フィルター条件
        const matchData = interviewData.filter((item) => {
            // AND条件：雇用形態、勤務場所
            const matchEmployment = !checkedEmployment || item.employmentType === checkedEmployment;
            const matchPlace = !checkedPlace || item.workPlace === checkedPlace;
            // OR条件：職種
            const matchTags = checkedTags.length === 0 || checkedTags.some((tag) => item.tags.includes(tag));

            return matchEmployment && matchPlace && matchTags;
        });

        renderData(matchData);
    }

    // イベントリスナーの設定
    [...controlEmployment].forEach((input) => input.addEventListener('change', getFilterInputs));
    [...controlPlace].forEach((input) => input.addEventListener('change', getFilterInputs));
    [...controlTags].forEach((input) => input.addEventListener('change', getFilterInputs));

    // リセットボタンの設定（リセット後は全件表示）
    controlResetBtn?.addEventListener('click', () => {
        [...controlEmployment].forEach((input) => {
            input.checked = false;
        });
        [...controlPlace].forEach((input) => {
            input.checked = false;
        });
        [...controlTags].forEach((input) => {
            input.checked = false;
        });
        renderData(interviewData);
    });

    // 初期表示
    renderData(interviewData);
})().catch((error) => {
    if (error instanceof Error) {
        throw new Error('非同期関数全体でエラーが発生しました');
    }
});
